import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Car, Check } from 'lucide-react';
import {
  ShadcnDialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Toast,
  ToastType,
  Badge,
} from '@/components/ui';
import { apiService } from '@/services/api';
import { Vehicle } from '@/types';
import { formatCurrency } from '@/utils/formatters';

interface VehicleLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName: string;
  onVehicleLinked: (vehicle: Vehicle) => void;
}

export const VehicleLinkDialog: React.FC<VehicleLinkDialogProps> = ({
  open,
  onOpenChange,
  leadId,
  leadName,
  onVehicleLinked,
}) => {
  const { t } = useTranslation();
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Load vehicles
  useEffect(() => {
    if (!open) return;

    const loadVehicles = async () => {
      setLoadingVehicles(true);
      try {
        const response = await apiService.getVehicles({
          status: 'available',
          search: searchQuery || undefined,
          limit: 20,
        });
        setVehicles(response.vehicles || []);
      } catch (error) {
        console.error('Error loading vehicles:', error);
        setToast({
          message: t('conversations.vehicleLink.loadError'),
          type: 'error',
        });
      } finally {
        setLoadingVehicles(false);
      }
    };

    const delayDebounce = setTimeout(loadVehicles, searchQuery ? 300 : 0);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery, open, t]);

  const handleLinkVehicle = async () => {
    if (!selectedVehicle) return;

    setLoading(true);
    try {
      await apiService.updateLead(leadId, {
        vehicleInterests: [
          {
            vehicleId: selectedVehicle.id,
            priority: 1,
          },
        ],
      });

      setToast({
        message: t('conversations.vehicleLink.linkSuccess'),
        type: 'success',
      });

      onVehicleLinked(selectedVehicle);
      handleClose();
    } catch (error: any) {
      console.error('Error linking vehicle:', error);
      setToast({
        message: error.message || t('conversations.vehicleLink.linkError'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedVehicle(null);
    onOpenChange(false);
  };

  const getVehicleDisplay = (vehicle: Vehicle) => {
    const brand = vehicle.brand?.name || '';
    const model = vehicle.model?.name || '';
    const year = vehicle.year || '';
    return `${brand} ${model} ${year}`.trim();
  };

  const getVehicleDetails = (vehicle: Vehicle) => {
    const details = [];
    const mileage = vehicle.mileage || vehicle.specifications?.mileage;
    if (mileage) details.push(`${mileage.toLocaleString()} km`);
    const color = vehicle.specifications?.body?.colorName || vehicle.specifications?.body?.color;
    if (color) details.push(color);
    const fuelType = vehicle.specifications?.engine?.type;
    if (fuelType) details.push(fuelType);
    return details.join(' • ');
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <ShadcnDialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t('conversations.vehicleLink.title')}</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {t('conversations.vehicleLink.subtitle', { leadName })}
            </p>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                placeholder={t('conversations.vehicleLink.searchPlaceholder')}
              />
            </div>

            {/* Vehicle List */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {loadingVehicles ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-muted-foreground">
                    {t('common.loading')}...
                  </div>
                </div>
              ) : vehicles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Car className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {searchQuery
                      ? t('conversations.vehicleLink.noResults')
                      : t('conversations.vehicleLink.noVehicles')}
                  </p>
                </div>
              ) : (
                vehicles.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    onClick={() => setSelectedVehicle(vehicle)}
                    className={`
                      w-full p-4 rounded-lg border-2 text-left transition-all
                      ${
                        selectedVehicle?.id === vehicle.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 bg-card'
                      }
                    `}
                  >
                    <div className="flex gap-4">
                      {/* Vehicle Image */}
                      {vehicle.photos && vehicle.photos.length > 0 ? (
                        <img
                          src={vehicle.photos[0].url}
                          alt={getVehicleDisplay(vehicle)}
                          className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Car className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}

                      {/* Vehicle Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground">
                              {getVehicleDisplay(vehicle)}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {getVehicleDetails(vehicle)}
                            </p>
                          </div>
                          {selectedVehicle?.id === vehicle.id && (
                            <Check className="h-5 w-5 text-primary flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <p className="font-bold text-lg text-foreground">
                            {formatCurrency(Number(vehicle.salePrice) || vehicle.price || 0)}
                          </p>
                          <Badge variant="success" size="sm">
                            {t('conversations.vehicleLink.available')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClose} variant="outline" disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleLinkVehicle}
              disabled={loading || !selectedVehicle}
            >
              {loading ? t('common.loading') : t('conversations.vehicleLink.linkVehicle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </ShadcnDialog>
    </>
  );
};
